import React, { useState, useRef, useEffect, ChangeEvent, KeyboardEvent } from 'react';
import { PrefixedTextField } from '@hz-design-system/web-ui';

export type TBiddingAmountInput = {
  id: string;
  prefixText: string;
  value?: string;
  labelText?: string;
  ariaLabel?: string;
  errorText?: string;
  onAmountChange: (value: string) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  isLive?: boolean;
};

const isValidBidKeyInput = (key: string) => {
  const validInputCharsRegex = new RegExp(/[\d,]/);
  return validInputCharsRegex.test(key);
};

const isValidAmountInputValue = (value: string) => {
  const commaSeparatedZeroToTwoDecomalsRegex = new RegExp(/^\d+,?\d{0,2}?$/g);
  return value === '' || commaSeparatedZeroToTwoDecomalsRegex.test(value);
};

const onKeyPress = (e: KeyboardEvent<HTMLInputElement>): void => {
  if (e.key !== 'Enter' && !isValidBidKeyInput(e.key)) {
    e.preventDefault();
  }
};

const numberToFormattedString = (num?: string): string => {
  if (num === null || num === undefined) {
    return '';
  }
  return num.toString().replace('.', ',');
};

const formattedStringToNumber = (str?: string): string => {
  if (!str) {
    return '';
  }
  const result = Number(str.replace(',', '.'));
  return isNaN(result) ? '' : result.toString();
};

const BiddingAmountInput = ({
  labelText,
  prefixText,
  id,
  value,
  ariaLabel,
  errorText,
  onAmountChange,
  inputRef,
  isLive,
}: TBiddingAmountInput) => {
  const [formattedValue, setFormattedValue] = useState(() => numberToFormattedString(value));
  const currentValue = useRef(value);
  currentValue.current = value;

  useEffect(() => {
    setFormattedValue(numberToFormattedString(value));
  }, [setFormattedValue, value]);

  const handleBlur = (e: ChangeEvent<HTMLInputElement>) => {
    onAmountChange(formattedStringToNumber(e.target.value));
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const valid = isValidAmountInputValue(e.target.value);

    if (valid) {
      setFormattedValue(e.target.value);
      const numberValue = formattedStringToNumber(e.target.value);
      if (currentValue.current !== numberValue) {
        onAmountChange(numberValue);
      }
    } else {
      e.preventDefault();
    }
  };

  return (
    <PrefixedTextField
      type="text"
      id={id}
      autoComplete="off"
      labelText={labelText}
      prefixText={prefixText}
      onKeyPress={onKeyPress}
      onChange={onChange}
      onBlur={handleBlur}
      value={formattedValue}
      errorText={errorText}
      // @ts-ignore
      ariaLabel={ariaLabel}
      inputRef={inputRef}
      isLive={isLive}
    />
  );
};

export default BiddingAmountInput;
